import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Loader2, Download, Plus, Search } from 'lucide-react';
import { CsvDownloadButton } from '@/components/CsvDownloadButton';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const auth = () => ({ headers: { Authorization: 'Bearer ' + localStorage.getItem('token') } });
const REASONS = ['price_difference', 'short_supply', 'excess_billing', 'damaged_goods', 'wrong_gst', 'invoice_mismatch', 'other'];
const STATUS_CLS = { draft: 'bg-slate-100 text-slate-700', approved: 'bg-blue-100 text-blue-800', sent_to_supplier: 'bg-amber-100 text-amber-800', closed: 'bg-emerald-100 text-emerald-800', cancelled: 'bg-red-100 text-red-700' };
const NEXT = { draft: ['approved', 'cancelled'], approved: ['sent_to_supplier', 'cancelled'], sent_to_supplier: ['closed'] };

export default function DebitNotes() {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [filters, setFilters] = React.useState({ status: '', grn_number: '', supplier: '', start_date: '', end_date: '' });
  const [showCreate, setShowCreate] = React.useState(false);
  const [ref, setRef] = React.useState('');
  const [grn, setGrn] = React.useState(null);
  const [lines, setLines] = React.useState({});
  const [busy, setBusy] = React.useState(false);

  const load = React.useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams(Object.entries(filters).filter(([, v]) => v));
    axios.get(API + '/accounts/debit-notes?' + p, auth())
      .then((r) => setRows(r.data.debit_notes))
      .catch((e) => toast.error(e.response?.data?.detail || 'Load failed'))
      .finally(() => setLoading(false));
  }, [filters]);
  React.useEffect(() => { load(); }, [load]);

  const lookup = () => {
    axios.get(API + '/accounts/grn-lookup/' + ref.trim(), auth())
      .then((r) => { setGrn(r.data.grn); setLines({}); })
      .catch((e) => toast.error(e.response?.data?.detail || 'GRN not found'));
  };

  const create = () => {
    const items = Object.entries(lines).filter(([, v]) => v.checked && Number(v.qty) > 0 && Number(v.amount) > 0)
      .map(([pid, v]) => ({ product_id: pid, quantity: Number(v.qty), debit_amount: Number(v.amount), reason: v.reason || 'price_difference', supplier_invoice_price: v.sip ? Number(v.sip) : null }));
    if (!items.length) return toast.error('Select at least one product with qty and debit amount');
    setBusy(true);
    axios.post(API + '/accounts/debit-notes', { grn_number: grn.grn_number, items }, auth())
      .then((r) => { toast.success('Created ' + r.data.debit_note.debit_note_number); setShowCreate(false); setGrn(null); setRef(''); load(); })
      .catch((e) => toast.error(e.response?.data?.detail || 'Create failed'))
      .finally(() => setBusy(false));
  };

  const setStatus = (id, status) => {
    axios.post(API + `/accounts/debit-notes/${id}/status`, { status }, auth())
      .then(() => { toast.success('Moved to ' + status.replace(/_/g, ' ')); load(); })
      .catch((e) => toast.error(e.response?.data?.detail || 'Action failed'));
  };

  const exportXlsx = () => {
    const p = new URLSearchParams(Object.entries(filters).filter(([, v]) => v));
    axios.get(API + '/accounts/debit-notes/export.xlsx?' + p, { ...auth(), responseType: 'blob' })
      .then((r) => {
        const url = window.URL.createObjectURL(new Blob([r.data]));
        const a = Object.assign(document.createElement('a'), { href: url, download: 'debit-notes.xlsx' });
        document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      }).catch(() => toast.error('Export failed'));
  };

  return (
    <div className="p-6 space-y-4" data-testid="debit-notes-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Debit Notes</h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportXlsx} data-testid="dn-export-btn"><Download className="w-4 h-4 mr-1" />Export</Button>
          <CsvDownloadButton onDownload={exportXlsx} testId="dn-export-csv" size="default" />
          <Button onClick={() => setShowCreate(true)} data-testid="dn-create-btn"><Plus className="w-4 h-4 mr-1" />New Debit Note</Button>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 items-end">
        <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className="h-9 rounded-md border px-2 text-sm" data-testid="dn-status-filter">
          <option value="">All statuses</option>{['draft', 'approved', 'sent_to_supplier', 'closed', 'cancelled'].map((s) => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
        </select>
        <Input placeholder="GRN No" className="h-9 w-40" value={filters.grn_number} onChange={(e) => setFilters({ ...filters, grn_number: e.target.value })} data-testid="dn-grn-filter" />
        <Input placeholder="Supplier" className="h-9 w-40" value={filters.supplier} onChange={(e) => setFilters({ ...filters, supplier: e.target.value })} data-testid="dn-supplier-filter" />
        <Input type="date" className="h-9 w-36" value={filters.start_date} onChange={(e) => setFilters({ ...filters, start_date: e.target.value })} data-testid="dn-start-date" />
        <Input type="date" className="h-9 w-36" value={filters.end_date} onChange={(e) => setFilters({ ...filters, end_date: e.target.value })} data-testid="dn-end-date" />
      </div>
      {loading ? <div className="flex justify-center py-12"><Loader2 className="w-7 h-7 animate-spin" /></div> : (
        <div className="overflow-x-auto border rounded-lg bg-white">
          <table className="w-full text-sm" data-testid="dn-table">
            <thead><tr className="border-b bg-slate-50 text-left">
              <th className="p-2">DN No</th><th className="p-2">Supplier</th><th className="p-2">GRN</th><th className="p-2">PO</th>
              <th className="p-2 text-right">Debit ₹</th><th className="p-2">Status</th><th className="p-2">By</th><th className="p-2">Date</th><th className="p-2">Actions</th>
            </tr></thead>
            <tbody>
              {rows.length === 0 ? <tr><td colSpan={9} className="p-8 text-center text-slate-400">No debit notes</td></tr> : rows.map((dn) => (
                <tr key={dn.id} className="border-b hover:bg-slate-50" data-testid={'dn-row-' + dn.debit_note_number}>
                  <td className="p-2 font-mono">{dn.debit_note_number}</td>
                  <td className="p-2">{dn.supplier_name}</td>
                  <td className="p-2 font-mono">{dn.grn_number}</td>
                  <td className="p-2 font-mono">{dn.po_number || '-'}</td>
                  <td className="p-2 text-right font-semibold">{dn.total_debit}</td>
                  <td className="p-2"><Badge className={STATUS_CLS[dn.status]}>{dn.status.replace(/_/g, ' ')}</Badge></td>
                  <td className="p-2 text-xs">{dn.created_by_name}</td>
                  <td className="p-2 text-xs">{(dn.created_at || '').slice(0, 10)}</td>
                  <td className="p-2"><div className="flex gap-1">
                    {(NEXT[dn.status] || []).map((s) => (
                      <Button key={s} size="sm" variant={s === 'cancelled' ? 'outline' : 'default'} className="h-7 text-xs" onClick={() => setStatus(dn.id, s)} data-testid={'dn-' + s + '-' + dn.debit_note_number}>{s.replace(/_/g, ' ')}</Button>
                    ))}
                  </div></td>
                </tr>))}
            </tbody>
          </table>
        </div>)}

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="dn-create-dialog">
          <DialogHeader><DialogTitle>Create Debit Note</DialogTitle></DialogHeader>
          <div className="flex gap-2">
            <Input placeholder="GRN Number or Supplier Invoice Number" value={ref} onChange={(e) => setRef(e.target.value)} data-testid="dn-lookup-input" />
            <Button onClick={lookup} data-testid="dn-lookup-btn"><Search className="w-4 h-4 mr-1" />Load</Button>
          </div>
          {grn && (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">{grn.supplier_name} · GRN {grn.grn_number} · PO {grn.po_number || '—'}</p>
              <table className="w-full text-xs border rounded">
                <thead><tr className="bg-slate-50 text-left"><th className="p-2">Sel</th><th className="p-2">Product</th><th className="p-2 text-right">Recv Qty</th><th className="p-2 text-right">GRN ₹</th><th className="p-2">Qty</th><th className="p-2">Supplier Inv ₹</th><th className="p-2">Debit ₹</th><th className="p-2">Reason</th></tr></thead>
                <tbody>{grn.items.map((it) => {
                  const l = lines[it.product_id] || {};
                  return (
                    <tr key={it.product_id} className="border-b">
                      <td className="p-2"><input type="checkbox" checked={!!l.checked} onChange={(e) => setLines({ ...lines, [it.product_id]: { ...l, checked: e.target.checked } })} data-testid={'dn-select-' + it.product_id} /></td>
                      <td className="p-2">{it.product_name}</td>
                      <td className="p-2 text-right">{it.received_quantity}</td>
                      <td className="p-2 text-right">{it.b2b_price_incl_gst}</td>
                      <td className="p-2">{(() => { const maxQ = (it.received_quantity || 0) - (it.already_debited_qty || 0); return (<><Input type="number" className="h-8 w-20" min="0" max={maxQ} value={l.qty ?? ''} onChange={(e) => { const v = e.target.value; setLines({ ...lines, [it.product_id]: { ...l, qty: v === '' ? '' : Math.min(Number(v), maxQ) } }); }} data-testid={'dn-qty-' + it.product_id} /><p className="text-[10px] text-slate-400">max {maxQ}</p></>); })()}</td>
                      <td className="p-2"><Input type="number" className="h-8 w-24" value={l.sip ?? ''} onChange={(e) => setLines({ ...lines, [it.product_id]: { ...l, sip: e.target.value } })} data-testid={'dn-sip-' + it.product_id} /></td>
                      <td className="p-2"><Input type="number" className="h-8 w-24" value={l.amount ?? ''} onChange={(e) => setLines({ ...lines, [it.product_id]: { ...l, amount: e.target.value } })} data-testid={'dn-amount-' + it.product_id} /></td>
                      <td className="p-2">
                        <select className="h-8 rounded border px-1" value={l.reason || 'price_difference'} onChange={(e) => setLines({ ...lines, [it.product_id]: { ...l, reason: e.target.value } })} data-testid={'dn-reason-' + it.product_id}>
                          {REASONS.map((r) => <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>)}
                        </select>
                      </td>
                    </tr>);
                })}</tbody>
              </table>
              <Button onClick={create} disabled={busy} data-testid="dn-submit-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Debit Note (Draft)'}</Button>
            </div>)}
        </DialogContent>
      </Dialog>
    </div>
  );
}
